(() => {
  "use strict";

  // --- Configuration ---
  const UTM_KEYS = [
    "utm_source", "utm_medium", "utm_campaign",
    "utm_content", "utm_term", "utm_id", "utm_variant",
    "utm_browser", // handled specially in IAB
    "ad_name", "adset_name", "omega_utm_source", "omega_utm_campaign", "omega_ad_name", "omega_adset_name", "campaign_id", "adset_id", "ad_id"
    ];
  const STORAGE_KEY = "persisted_utm_params_v1";
  const HASH_MIRROR_KEY = "utm_mirror"; // mirrors ALL query params
  const WRITE_UTM_COOKIES = true;       // first-party attribution cookies
  const COOKIE_PREFIX = "_utm_";
  const COOKIE_MAX_AGE_DAYS = 30;

  // --- In-App Browser Detection (includes Slack) ---
  function isInAppBrowser() {
    const ua = (navigator.userAgent || navigator.vendor || window.opera || "").toLowerCase();
    const patterns = [
      "fban", "fbav", "fb_iab", "instagram", "messenger",
      "tiktok", "capcut", "snapchat", "pinterest", "linkedinapp",
      "reddit", "quora", "line/", "viber", "wechat", "whatsapp",
      "gsa", "duckduckgo", "youtube", "slack", " wv"
    ];
    return patterns.some(p => ua.includes(p));
  }

  // --- Base64url helpers (safe for URL fragments) ---
  function b64urlEncode(str) {
    try {
      return btoa(unescape(encodeURIComponent(str))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/,"");
    } catch { return ""; }
  }
  function b64urlDecode(str) {
    try {
      let s = (str || "").replace(/-/g, "+").replace(/_/g, "/");
      while (s.length % 4) s += "=";
      return decodeURIComponent(escape(atob(s)));
    } catch { return ""; }
  }

  // --- Query/UTM utils ---
  function getAllQueryParams() {
    const u = new URL(window.location.href);
    const obj = {};
    for (const [k, v] of u.searchParams.entries()) obj[k] = v;
    return obj;
  }

  function getCurrentUtmParams() {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    let found = false;
    for (const key of UTM_KEYS) {
      if (params.has(key)) {
        utm[key] = params.get(key);
        found = true;
      }
    }
    return found ? utm : null;
  }

  function loadPersistedUtm() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  function persistUtm(utmObj) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utmObj)); } catch {}
  }

  function isInternalUrl(url) {
    try {
      const u = new URL(url, document.baseURI);
      return u.origin === window.location.origin;
    } catch { return true; }
  }

  function shouldSkip(el) {
    // Skip links with data-utm-skip="true"
    return el.closest("[data-utm-skip='true']") || el.getAttribute("data-utm-skip") === "true";
  }

  function ensureQueryParamOnCurrentUrl(key, value) {
    const u = new URL(window.location.href);
    if (u.searchParams.get(key) !== value) {
      u.searchParams.set(key, value);
      history.replaceState({}, "", u.toString());
    }
  }

  function removeQueryParamOnCurrentUrl(key) {
    const u = new URL(window.location.href);
    if (u.searchParams.has(key)) {
      u.searchParams.delete(key);
      history.replaceState({}, "", u.toString());
    }
  }

  // --- Hash (fragment) helpers ---
  function parseHashParams(hash) {
    const h = (hash || window.location.hash || "").replace(/^#/, "");
    const map = {};
    if (!h) return map;
    h.split("&").forEach(kv => {
      const i = kv.indexOf("=");
      if (i === -1) return;
      const k = decodeURIComponent(kv.slice(0, i));
      const v = decodeURIComponent(kv.slice(i + 1));
      map[k] = v;
    });
    return map;
  }

  function serializeHashParams(obj, originalHash) {
    const existing = parseHashParams(originalHash);
    const out = [];
    for (const [k, v] of Object.entries(existing)) {
      if (k === HASH_MIRROR_KEY) continue; // we will overwrite
      out.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
    }
    if (obj && obj[HASH_MIRROR_KEY]) {
      out.push(encodeURIComponent(HASH_MIRROR_KEY) + "=" + encodeURIComponent(obj[HASH_MIRROR_KEY]));
    }
    return out.length ? "#" + out.join("&") : "";
  }

  function ensureHashMirrorOnCurrentUrl(paramsObj) {
    const encoded = b64urlEncode(JSON.stringify(paramsObj || {}));
    const u = new URL(window.location.href);
    const newHash = serializeHashParams({ [HASH_MIRROR_KEY]: encoded }, u.hash);
    if (newHash !== u.hash) {
      u.hash = newHash;
      history.replaceState({}, "", u.toString());
    }
  }

  // --- Link rewriting ---
  function appendParamsToUrl(href, baseParams, { forceFlags = {} , alsoMirrorInHash = false } = {}) {
    const u = new URL(href, document.baseURI);

    // 1) Add/complete UTMs (do not overwrite existing)
    for (const key of UTM_KEYS) {
      if (key in (baseParams || {})) {
        if (!u.searchParams.has(key)) u.searchParams.set(key, baseParams[key]);
      }
    }

    // 2) Force flags (always overwrite)
    for (const [k, v] of Object.entries(forceFlags)) {
      if (v != null) u.searchParams.set(k, String(v));
    }

    // 3) Mirror ALL current target URL params into hash
    if (alsoMirrorInHash) {
      const allForTarget = {};
      for (const [k, v] of u.searchParams.entries()) allForTarget[k] = v;
      const encoded = b64urlEncode(JSON.stringify(allForTarget));
      u.hash = serializeHashParams({ [HASH_MIRROR_KEY]: encoded }, u.hash);
    }
    return u.toString();
  }

  function rewriteAnchor(anchor, utmBase, forceFlags, alsoMirrorInHash) {
    if (!anchor || shouldSkip(anchor)) return;
    const href = anchor.getAttribute("href");
    if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
    if (!isInternalUrl(href)) return;

    const newHref = appendParamsToUrl(href, utmBase, { forceFlags, alsoMirrorInHash });
    if (newHref !== href) anchor.setAttribute("href", newHref);
  }

  function rewriteAllLinks(utmBase, forceFlags, alsoMirrorInHash) {
    document.querySelectorAll('a[href]').forEach(a => rewriteAnchor(a, utmBase, forceFlags, alsoMirrorInHash));
  }

  function observeMutations(utmBase, forceFlags, alsoMirrorInHash) {
    const mo = new MutationObserver(mutations => {
      for (const m of mutations) {
        m.addedNodes && m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('a[href]')) rewriteAnchor(node, utmBase, forceFlags, alsoMirrorInHash);
          const innerLinks = node.querySelectorAll ? node.querySelectorAll('a[href]') : [];
          innerLinks.forEach(a => rewriteAnchor(a, utmBase, forceFlags, alsoMirrorInHash));
        });
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  function interceptClicks(utmBase, forceFlags, alsoMirrorInHash) {
    document.addEventListener("click", (ev) => {
      const a = ev.target.closest && ev.target.closest("a[href]");
      if (!a) return;
      rewriteAnchor(a, utmBase, forceFlags, alsoMirrorInHash);
    }, true);
  }

  // --- Cookies (optional) ---
  function setCookie(name, value, days) {
    try {
      const d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      const secure = location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Expires=${d.toUTCString()}; Path=/; SameSite=Lax${secure}`;
    } catch {}
  }

  function captureAttributionToCookies(utm) {
    if (!WRITE_UTM_COOKIES || !utm) return;
    for (const k of UTM_KEYS) {
      if (utm[k]) setCookie(COOKIE_PREFIX + k, utm[k], COOKIE_MAX_AGE_DAYS);
    }
  }

  // --- Restore from hash (outside IAB), skipping certain keys ---
  function restoreFromHashIfMissing({ skipKeys = [] } = {}) {
    const map = parseHashParams();
    const payload = map[HASH_MIRROR_KEY];
    if (!payload) return false;
    const json = b64urlDecode(payload);
    if (!json) return false;

    let mirrored;
    try { mirrored = JSON.parse(json); } catch { return false; }
    if (!mirrored || typeof mirrored !== "object") return false;

    const u = new URL(window.location.href);
    let changed = false;
    for (const [k, v] of Object.entries(mirrored)) {
      if (skipKeys.includes(k)) continue; // do not restore skipped keys (e.g., screenwidth)
      if (!u.searchParams.has(k)) {
        u.searchParams.set(k, v);
        changed = true;
      }
    }
    if (changed) {
      // Drop our mirror entry but keep other fragment parts
      u.hash = serializeHashParams({}, u.hash);
      history.replaceState({}, "", u.toString());
    }
    return changed;
  }

  // --- Flow ---
  const IN_APP = isInAppBrowser();
  const SCREEN_WIDTH = String(screen && screen.width ? screen.width : "");

  // 1) Persist UTMs found at landing (session-scoped)
  const landedUtm = getCurrentUtmParams();
  if (landedUtm) persistUtm(landedUtm);

  // 2) Prepare base UTMs from session (used for link completion)
  const utmBase = loadPersistedUtm() || {};

  // 3) Force flags (only while in-app): utm_browser=inapp + screenwidth=<px>
  const forceFlags = {};
  if (IN_APP) {
    forceFlags["utm_browser"] = "inapp";
    if (SCREEN_WIDTH) forceFlags["screenwidth"] = SCREEN_WIDTH;
  }

  // 4) Ensure current URL carries required flags (only in-app)
  if (IN_APP) {
    for (const [k, v] of Object.entries(forceFlags)) ensureQueryParamOnCurrentUrl(k, String(v));
  }

  if (IN_APP) {
    // Mirror ALL current query params for reliable app→browser handoff
    ensureHashMirrorOnCurrentUrl(getAllQueryParams());

    // Append session UTMs (non-overwrite), enforce flags (incl. screenwidth), and mirror ALL params on targets
    rewriteAllLinks(utmBase, forceFlags, /* alsoMirrorInHash */ true);
    observeMutations(utmBase, forceFlags, true);
    interceptClicks(utmBase, forceFlags, true);
  } else {
    // Outside IAB:
    // a) If the app stripped some/all query params, restore them from hash — but SKIP 'screenwidth'
    restoreFromHashIfMissing({ skipKeys: ["screenwidth"] });

    // b) If 'screenwidth' survived in the URL, remove it now (do not touch other params)
    removeQueryParamOnCurrentUrl("screenwidth");

    // c) Optional: keep attribution cookies for UTMs only (doesn't alter URL)
    const nowUtm = getCurrentUtmParams();
    if (nowUtm) captureAttributionToCookies(nowUtm);
  }
})();



(function () {
  // Set cookie + alert
  function setCookieAndAlert() {
    try {
      // Persist 30 days; tweak if you want a session cookie instead
      const expires = new Date(Date.now() + 30*24*60*60*1000).toUTCString();
      const host = location.hostname.replace(/^www\./, "");
      // Using a leading dot domain helps across subdomains; remove if it causes issues
      document.cookie = `ss-session-id=0; expires=${expires}; path=/; domain=.${host}; SameSite=Lax`;
    } catch (err) {
      console.error("Cookie set error:", err);
    }
    //alert("0");
  }

  // 1) Event delegation in CAPTURE phase (wins against stopPropagation in other code)
  document.addEventListener("click", function (e) {
    const hit = e.target.closest(".footer-bottom");
    if (!hit) return;
    // Prevent navigation so the alert is visible before the page changes
    e.preventDefault();
    setCookieAndAlert();
  }, true);

  // 2) Also bind directly to any current .footer-bottom nodes (if you prefer direct binding)
  function bindDirect() {
    document.querySelectorAll(".footer-bottom").forEach(el => {
      el.addEventListener("click", function (e) {
        e.preventDefault();
        setCookieAndAlert();
      }, { capture: true });
    });

    // Debug helper
    if (!document.querySelector(".footer-bottom")) {
      console.warn("No element with .footer-bottom found at init");
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bindDirect);
  } else {
    bindDirect();
  }
})();